import { useEffect, useState, useCallback } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Copy, Plus, Check, X, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { NewAffiliateDialog } from '@/components/admin/NewAffiliateDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface AffiliateRow {
  id: string;
  code: string;
  name: string;
  email: string;
  commission_pct: number;
  status: string;
  notes: string | null;
  created_at: string;
  signups: number;
  paying_users: number;
  total_commissions_cents: number;
  unpaid_balance_cents: number;
}

interface ApplicationRow {
  id: string;
  full_name: string;
  email: string;
  company: string | null;
  url: string | null;
  audience: string;
  monthly_reach: string;
  channels: string[];
  status: 'pending' | 'approved' | 'rejected';
  notes: string | null;
  created_at: string;
}

function formatDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function statusVariant(status: string): 'default' | 'secondary' | 'destructive' {
  if (status === 'active') return 'default';
  if (status === 'paused') return 'secondary';
  return 'destructive';
}

function suggestCode(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 16) || 'partner';
}

export default function AdminAffiliates() {
  const { toast } = useToast();
  const [rows, setRows] = useState<AffiliateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Applications state
  const [apps, setApps] = useState<ApplicationRow[]>([]);
  const [appsLoading, setAppsLoading] = useState(true);
  const [approveTarget, setApproveTarget] = useState<ApplicationRow | null>(null);
  const [rejectTarget, setRejectTarget] = useState<ApplicationRow | null>(null);
  const [actioning, setActioning] = useState(false);
  const [approveCode, setApproveCode] = useState('');
  const [approvePct, setApprovePct] = useState('20');
  const [approveNotes, setApproveNotes] = useState('');
  const [rejectNotes, setRejectNotes] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('admin_list_affiliates');
    setLoading(false);
    if (error) {
      toast({ title: 'Failed to load affiliates', description: error.message, variant: 'destructive' });
      setRows([]);
      return;
    }
    setRows((data ?? []) as AffiliateRow[]);
  }, [toast]);

  const refreshApps = useCallback(async () => {
    setAppsLoading(true);
    const { data, error } = await supabase.rpc('admin_list_affiliate_applications', { p_status: null });
    setAppsLoading(false);
    if (error) {
      toast({ title: 'Failed to load applications', description: error.message, variant: 'destructive' });
      setApps([]);
      return;
    }
    setApps((data ?? []) as ApplicationRow[]);
  }, [toast]);

  useEffect(() => {
    refresh();
    refreshApps();
  }, [refresh, refreshApps]);

  async function toggleStatus(row: AffiliateRow) {
    const next = row.status === 'active' ? 'paused' : 'active';
    const { data, error } = await supabase.rpc('admin_set_affiliate_status', {
      p_affiliate_id: row.id,
      p_status: next,
    });
    if (error || !(data as { success?: boolean })?.success) {
      toast({
        title: 'Could not update status',
        description: error?.message ?? (data as { error?: string })?.error ?? 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    refresh();
  }

  function copyLink(code: string) {
    const link = `${window.location.origin}/?ref=${encodeURIComponent(code)}`;
    navigator.clipboard.writeText(link).then(
      () => toast({ title: 'Link copied', description: link }),
      () => toast({ title: 'Copy failed', variant: 'destructive' }),
    );
  }

  function openApprove(app: ApplicationRow) {
    setApproveTarget(app);
    setApproveCode(suggestCode(app.full_name));
    setApprovePct('20');
    setApproveNotes('');
  }

  function openReject(app: ApplicationRow) {
    setRejectTarget(app);
    setRejectNotes('');
  }

  async function confirmApprove() {
    if (!approveTarget) return;
    setActioning(true);
    const { data, error } = await supabase.rpc('admin_approve_affiliate_application', {
      p_app_id: approveTarget.id,
      p_code: approveCode.trim(),
      p_commission_pct: Number(approvePct) || 20,
      p_notes: approveNotes.trim() || null,
    });
    const result = (data || {}) as { success?: boolean; error?: string; code?: string; email?: string; full_name?: string };
    if (error || !result.success) {
      setActioning(false);
      toast({
        title: 'Could not approve',
        description: error?.message ?? result.error ?? 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    // Send welcome email
    try {
      await supabase.functions.invoke('notify-affiliate-decision', {
        body: {
          decision: 'approved',
          email: result.email,
          full_name: result.full_name,
          code: result.code,
          commission_pct: Number(approvePct) || 20,
        },
      });
    } catch (e) {
      console.error(e);
    }
    setActioning(false);
    setApproveTarget(null);
    toast({ title: 'Affiliate approved', description: `Welcome email sent to ${result.email}` });
    refresh();
    refreshApps();
  }

  async function confirmReject() {
    if (!rejectTarget) return;
    setActioning(true);
    const { data, error } = await supabase.rpc('admin_reject_affiliate_application', {
      p_app_id: rejectTarget.id,
      p_notes: rejectNotes.trim() || null,
    });
    const result = (data || {}) as { success?: boolean; error?: string; email?: string; full_name?: string };
    if (error || !result.success) {
      setActioning(false);
      toast({
        title: 'Could not reject',
        description: error?.message ?? result.error ?? 'Unknown error',
        variant: 'destructive',
      });
      return;
    }
    try {
      await supabase.functions.invoke('notify-affiliate-decision', {
        body: {
          decision: 'rejected',
          email: result.email,
          full_name: result.full_name,
        },
      });
    } catch (e) {
      console.error(e);
    }
    setActioning(false);
    setRejectTarget(null);
    toast({ title: 'Application rejected', description: `Rejection email sent to ${result.email}` });
    refreshApps();
  }

  const pendingApps = apps.filter((a) => a.status === 'pending');
  const reviewedApps = apps.filter((a) => a.status !== 'pending').slice(0, 20);

  return (
    <AdminLayout>
      <div className="p-6 space-y-8">
        {/* Applications */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-white">Affiliate applications</h1>
              <p className="text-sm text-[#9ca3af]">
                Review and approve incoming applications from <code>/affiliates</code>.
              </p>
            </div>
            <Badge variant={pendingApps.length > 0 ? 'default' : 'secondary'}>
              {pendingApps.length} pending
            </Badge>
          </div>

          <div className="rounded-md border border-[#1e2530] overflow-hidden bg-[#0d1117]">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1e2530] hover:bg-transparent">
                  <TableHead className="text-[#9ca3af]">Submitted</TableHead>
                  <TableHead className="text-[#9ca3af]">Name</TableHead>
                  <TableHead className="text-[#9ca3af]">Email</TableHead>
                  <TableHead className="text-[#9ca3af]">Reach</TableHead>
                  <TableHead className="text-[#9ca3af]">Channels</TableHead>
                  <TableHead className="text-[#9ca3af]">URL</TableHead>
                  <TableHead className="text-[#9ca3af]">Audience</TableHead>
                  <TableHead className="text-[#9ca3af]">Status</TableHead>
                  <TableHead className="text-[#9ca3af] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appsLoading && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-[#9ca3af] py-6">Loading…</TableCell>
                  </TableRow>
                )}
                {!appsLoading && pendingApps.length === 0 && reviewedApps.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-[#9ca3af] py-6">No applications yet.</TableCell>
                  </TableRow>
                )}
                {!appsLoading && [...pendingApps, ...reviewedApps].map((a) => (
                  <TableRow key={a.id} className="border-[#1e2530]">
                    <TableCell className="text-[#9ca3af] text-xs whitespace-nowrap">
                      {new Date(a.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-white">{a.full_name}</TableCell>
                    <TableCell className="text-[#d1d5db]">{a.email}</TableCell>
                    <TableCell className="text-[#d1d5db]">{a.monthly_reach}</TableCell>
                    <TableCell className="text-[#d1d5db] text-xs max-w-[200px] truncate">
                      {a.channels?.join(', ') || '—'}
                    </TableCell>
                    <TableCell className="text-[#d1d5db] text-xs max-w-[150px] truncate">
                      {a.url ? (
                        <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-[#14b8a6] hover:underline">
                          {a.url.replace(/^https?:\/\//, '')}
                        </a>
                      ) : '—'}
                    </TableCell>
                    <TableCell className="text-[#d1d5db] text-xs max-w-[300px] truncate" title={a.audience}>
                      {a.audience}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={a.status === 'pending' ? 'default' : a.status === 'approved' ? 'secondary' : 'destructive'}
                        className="capitalize"
                      >
                        {a.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {a.status === 'pending' ? (
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="default" onClick={() => openApprove(a)} className="gap-1">
                            <Check className="w-3 h-3" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => openReject(a)} className="gap-1">
                            <X className="w-3 h-3" /> Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-[#6b7280]">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        {/* Existing affiliates table */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">Affiliates</h2>
              <p className="text-sm text-[#9ca3af]">Internal affiliate tracking — admin only.</p>
            </div>
            <Button onClick={() => setDialogOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" /> New affiliate
            </Button>
          </div>

          <div className="rounded-md border border-[#1e2530] overflow-hidden bg-[#0d1117]">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1e2530] hover:bg-transparent">
                  <TableHead className="text-[#9ca3af]">Name</TableHead>
                  <TableHead className="text-[#9ca3af]">Email</TableHead>
                  <TableHead className="text-[#9ca3af]">Code</TableHead>
                  <TableHead className="text-[#9ca3af] text-right">Comm %</TableHead>
                  <TableHead className="text-[#9ca3af] text-right">Signups</TableHead>
                  <TableHead className="text-[#9ca3af] text-right">Paying</TableHead>
                  <TableHead className="text-[#9ca3af] text-right">Earned</TableHead>
                  <TableHead className="text-[#9ca3af] text-right">Unpaid</TableHead>
                  <TableHead className="text-[#9ca3af]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={9} className="text-center text-[#9ca3af] py-6">Loading…</TableCell></TableRow>
                )}
                {!loading && rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-[#9ca3af] py-6">No affiliates yet.</TableCell></TableRow>
                )}
                {!loading && rows.map((r) => (
                  <TableRow key={r.id} className="border-[#1e2530]">
                    <TableCell className="text-white">{r.name}</TableCell>
                    <TableCell className="text-[#d1d5db]">{r.email}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => copyLink(r.code)}
                        className="inline-flex items-center gap-1.5 text-[#14b8a6] hover:underline font-mono text-sm"
                        title="Copy referral link"
                      >
                        {r.code} <Copy className="w-3 h-3" />
                      </button>
                    </TableCell>
                    <TableCell className="text-right text-[#d1d5db]">{Number(r.commission_pct).toFixed(2)}%</TableCell>
                    <TableCell className="text-right text-[#d1d5db]">{r.signups}</TableCell>
                    <TableCell className="text-right text-[#d1d5db]">{r.paying_users}</TableCell>
                    <TableCell className="text-right text-[#d1d5db]">{formatDollars(r.total_commissions_cents)}</TableCell>
                    <TableCell className="text-right text-[#d1d5db]">{formatDollars(r.unpaid_balance_cents)}</TableCell>
                    <TableCell>
                      <button onClick={() => toggleStatus(r)} title="Toggle active/paused">
                        <Badge variant={statusVariant(r.status)} className="capitalize cursor-pointer">{r.status}</Badge>
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>

      <NewAffiliateDialog open={dialogOpen} onOpenChange={setDialogOpen} onCreated={refresh} />

      {/* Approve dialog */}
      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve affiliate — {approveTarget?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="code">Referral code</Label>
              <Input
                id="code"
                value={approveCode}
                onChange={(e) => setApproveCode(e.target.value)}
                placeholder="lowercase, no spaces"
              />
              <p className="text-xs text-muted-foreground mt-1">2–32 chars, letters/numbers/dash/underscore.</p>
            </div>
            <div>
              <Label htmlFor="pct">Commission %</Label>
              <Input id="pct" type="number" step="0.5" value={approvePct} onChange={(e) => setApprovePct(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="approve-notes">Internal notes (optional)</Label>
              <Textarea id="approve-notes" rows={2} value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveTarget(null)} disabled={actioning}>Cancel</Button>
            <Button onClick={confirmApprove} disabled={actioning || !approveCode.trim()}>
              {actioning && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Approve & send welcome email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject application — {rejectTarget?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              A polite rejection email will be sent. Internal notes are private.
            </p>
            <div>
              <Label htmlFor="reject-notes">Internal notes (optional)</Label>
              <Textarea id="reject-notes" rows={3} value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTarget(null)} disabled={actioning}>Cancel</Button>
            <Button variant="destructive" onClick={confirmReject} disabled={actioning}>
              {actioning && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Reject & send email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
